import { computed } from "vue";
import { DS_TABLE_CLASSES } from "@/src/design_system/data_display/table/constants/classes";
import type { DSTableColumn, DSTableProps } from "@/src/design_system/data_display/table/types/props";

type HeaderSpan = { key: string | null; label: string; colspan: number; startIndex: number; endIndex: number };

const extractAvailableColumns = (keys: readonly string[], available: Map<string, DSTableColumn>): DSTableColumn[] =>
  keys.flatMap(key => {
    const column = available.get(key);
    if (!column) return [];
    available.delete(key);
    return [column];
  });

export const useDSTableLayout = (props: DSTableProps) => {
  const layout = computed(() => {
    const available = new Map(props.columns.map(column => [column.key, column]));
    const start = extractAvailableColumns(props.stickyStartColumns ?? [], available);
    const end = extractAvailableColumns(props.stickyEndColumns ?? [], available);
    const middle = props.columns.filter(column => available.has(column.key));
    return {
      columns: [...start, ...middle, ...end],
      startCount: start.length,
      endBoundary: start.length + middle.length,
    };
  });
  const displayedColumns = computed(() => layout.value.columns);
  const isStickyStart = (index: number) => index < layout.value.startCount;
  const isStickyEnd = (index: number) => index >= layout.value.endBoundary && index < displayedColumns.value.length;
  const precedesStickyEnd = (index: number) =>
    layout.value.endBoundary < displayedColumns.value.length && index === layout.value.endBoundary - 1;
  const stickyClasses = (index: number) => ({
    [DS_TABLE_CLASSES.StickyStart]: isStickyStart(index),
    [DS_TABLE_CLASSES.StickyEnd]: isStickyEnd(index),
    [DS_TABLE_CLASSES.StickyEndBoundary]: isStickyEnd(index) && index === layout.value.endBoundary,
  });
  const sameStickySegment = (first: number, second: number) =>
    isStickyStart(first) === isStickyStart(second) && isStickyEnd(first) === isStickyEnd(second);
  const extendsSpan = (previous: HeaderSpan, key: string | null, index: number) =>
    previous.key === key && sameStickySegment(previous.endIndex, index);
  const displayedGroups = computed(() => {
    const groups = new Map(
      props.headerGroups?.flatMap(group => group.columnKeys.map(key => [key, group] as const)) ?? []
    );
    const spans: HeaderSpan[] = [];
    displayedColumns.value.forEach((column, index) => {
      const group = groups.get(column.key);
      const key = group ? group.key : null;
      const previous = spans.at(-1);
      if (previous && extendsSpan(previous, key, index)) {
        previous.colspan += 1;
        previous.endIndex = index;
      } else {
        spans.push({ key, label: group ? group.label : "", colspan: 1, startIndex: index, endIndex: index });
      }
    });
    return spans.map((span, index) => ({
      ...span,
      rightBorder: index < spans.length - 1 && span.key !== spans[index + 1].key && !precedesStickyEnd(span.endIndex),
    }));
  });
  const cellClasses = (column: DSTableColumn, index: number) => [
    DS_TABLE_CLASSES.Cell,
    { [DS_TABLE_CLASSES.RightBorder]: column.rightBorder && !precedesStickyEnd(index), ...stickyClasses(index) },
  ];
  const groupHeaderClasses = (group: HeaderSpan & { rightBorder: boolean }) => [
    DS_TABLE_CLASSES.Cell,
    DS_TABLE_CLASSES.Header,
    DS_TABLE_CLASSES.GroupHeader,
    { [DS_TABLE_CLASSES.RightBorder]: group.rightBorder, ...stickyClasses(group.startIndex) },
  ];

  return { displayedColumns, displayedGroups, isStickyStart, isStickyEnd, cellClasses, groupHeaderClasses };
};

export type DSTableLayout = ReturnType<typeof useDSTableLayout>;
